import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Pipeline.module.scss';
import { useGetKpiSettingsQuery } from '../../../../redux/services/kpiSettingsByTypeAPI';
import { useProfileParams } from '../../../../utils/common';
import InsightAI from '../../../common/InsightAI/InsightAI';
import InteractiveTileGrid from '../InteractiveTileGrid/InteractiveTileGrid';
import ABOApplicationsTile, {
  ABOApplicationsDetail,
} from './KPI/ABOApplications/ABOApplicationsTile';
import ProductivityPerContributorTile, {
  ProductivityPerContributorDetail,
} from './KPI/ProductivityPerContributor/ProductivityPerContributorTile';
import { useGetBusinessPerformanceSummaryQuery } from '../../../../redux/services/businessPerformaceAPI';
import isEmpty from 'lodash/isEmpty';
import ActivationRateTile, { ActivationRateDetail } from './KPI/ActivationRate/ActivationRateTile';
import AverageBronzeTile, { AverageBronzeDetail } from './KPI/AverageBronze/AverageBronzeTile';

const Pipeline: React.FC<CategoriesProps> = () => {
  const { t } = useTranslation();
  const [pipelineKPIsCodelist, setPipelineKPIsCodelist] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const { affiliateCode, aboNumber } = useProfileParams();

  const { data: pipelineKpisResponse } = useGetKpiSettingsQuery(
    {
      type: 'business-performance-pipeline',
      affiliateCode,
    },
    { refetchOnMountOrArgChange: true }
  );

  const { data: performanceSummaryResponse, isLoading } = useGetBusinessPerformanceSummaryQuery(
    { affiliateCode, aboNumber, payload: { kpis: pipelineKPIsCodelist } },
    {
      refetchOnMountOrArgChange: true,
      skip: Number(affiliateCode) === 0 || aboNumber === 0 || isEmpty(pipelineKPIsCodelist),
    }
  );

  useEffect(() => {
    if (!isEmpty(pipelineKPIsCodelist)) {
      setLoading(isLoading);
    }
  }, [isLoading, pipelineKPIsCodelist]);

  useEffect(() => {
    const response = pipelineKpisResponse?.body || [];

    const kpiCodes = response.map((item: { code: string }) => {
      return {
        code: item?.code === 'activationRate:90|30|60' ? 'activationRate90' : item?.code,
      };
    });

    setPipelineKPIsCodelist(kpiCodes);
  }, [pipelineKpisResponse]);

  // Extract individual KPI data from summary response
  const kpisData = performanceSummaryResponse?.body?.kpis || [];
  const aboAppsSummary = kpisData.find((kpi: any) => kpi.kpiCode === 'aboApplications');
  const activationRateSummary = kpisData.find((kpi: any) => kpi.kpiCode === 'activationRate90');
  const productivitySummary = kpisData.find(
    (kpi: any) => kpi.kpiCode === 'productivityPerContributor'
  );
  const averageBronzeSummary = kpisData.find((kpi: any) => kpi.kpiCode === 'averageBronze');

  return (
    <div className={styles.pipeline_wrapper}>
      <div className={styles.heading_area}>
        <div className={styles.heading}>{t('pipeline')}</div>
      </div>
      <InsightAI
        content={'AiContent is here'}
        className={styles.insight_ai}
        showDropdown={true}
        updateDate="08/19/2024"
        isDropdownEnabled={true}
        flowerIconSize={24}
        title={'ai_summary_pipeline'}
      />
      <InteractiveTileGrid
        className={styles.tiles_grid_container}
        tiles={[
          {
            id: 'aboApplications',
            kpiTile: (
              <ABOApplicationsTile
                summary={aboAppsSummary}
                loading={loading}
                affiliateCode={affiliateCode}
              />
            ),
            kpiDetailTile: <ABOApplicationsDetail kpiCode="aboApplications" />,
          },
          {
            id: 'activationRate',
            kpiTile: (
              <ActivationRateTile
                summary={activationRateSummary}
                loading={loading}
                affiliateCode={affiliateCode}
              />
            ),
            kpiDetailTile: <ActivationRateDetail kpiCode="activationRate90" />,
          },
          {
            id: 'productivityPerContributor',
            kpiTile: (
              <ProductivityPerContributorTile
                summary={productivitySummary}
                loading={loading}
                affiliateCode={affiliateCode}
              />
            ),
            kpiDetailTile: (
              <ProductivityPerContributorDetail kpiCode="productivityPerContributor" />
            ),
          },
          {
            id: 'averageBronze',
            kpiTile: <AverageBronzeTile summary={averageBronzeSummary} loading={loading} />,
            kpiDetailTile: <AverageBronzeDetail kpiCode="averageBronze" />,
          },
        ]}
      />
    </div>
  );
};

export default Pipeline;
